import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	input,
	InputSignal,
	OnDestroy,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	ViewChild,
	WritableSignal,
} from '@angular/core'
import { fromEvent, Subject, takeUntil, throttleTime } from 'rxjs'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OptionsBoardSettingsInterface } from '@shared/components/trading/widgets/options-board-widget/core/interfaces/options-board-settings.interface'
import { OptionsBoardColumnsEnum } from '@shared/components/trading/widgets/options-board-widget/core/enums/options-board-columns.enum'
import { DirectionEnum } from '@shared/enums/direction.enum'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import { PricePipe } from '@shared/pipes/price.pipe'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { OptionsBoardTableActionsComponent } from '@shared/components/trading/options-board/common/options-board-table/common/options-board-table-actions/options-board-table-actions.component'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { OptionContractInterface } from '@modules/options-module/general/interfaces/options-contract.interface'
import { TableDynamicRowComponent } from '@shared/components/common/table/core/components/table-dynamic-row/table-dynamic-row.component'
import { TranslatePipe } from '@ngx-translate/core'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { RowInterface } from '@shared/components/common/table/core/interfaces/row.interface'
import { DynamicRowIdConstant } from '@shared/components/common/table/core/constants/dynamic-row-id.constant'
import { TableComponent } from '@shared/components/common/table/table.component'
import { AnimatedNumberDirective } from '@shared/directives/animated-number/animated-number.directive'

@UseLocales({ ru, en })
@Component({
	selector: 'app-options-board-table',
	templateUrl: './options-board-table.component.html',
	styleUrl: './options-board-table.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		PricePipe,
		HighlightDirective,
		QuantityPipe,
		OptionsBoardTableActionsComponent,
		TableColumnComponent,
		TableActionsComponent,
		ProfitPipe,
		TableDynamicRowComponent,
		TranslatePipe,
		TableComponent,
		AnimatedNumberDirective,
	],
})
export class OptionsBoardTableComponent implements AfterViewInit, OnDestroy {
	protected readonly directionEnum: typeof DirectionEnum = DirectionEnum
	protected readonly optionsBoardColumnsEnum: typeof OptionsBoardColumnsEnum = OptionsBoardColumnsEnum
	protected readonly dynamicRowIdConstant: string = DynamicRowIdConstant

	@ViewChild('optionBoard', { static: false }) optionBoardRef: ElementRef<HTMLDivElement> | null = null
	@ViewChild('dynamicPriceRow', { static: false }) dynamicPriceRowRef: ElementRef<HTMLDivElement> | null = null

	public widget: InputSignal<WidgetInterface<OptionsBoardSettingsInterface> | null> =
		input<WidgetInterface<OptionsBoardSettingsInterface> | null>(null)
	public liveTicker: InputSignal<LiveTickerInterface | null> = input<LiveTickerInterface | null>(null)
	public options: InputSignal<OptionContractInterface[]> = input<OptionContractInterface[]>([])
	public isLoadingOptions: InputSignal<boolean> = input<boolean>(false)
	public isLoadingTop: InputSignal<boolean> = input<boolean>(false)
	public isLoadingBottom: InputSignal<boolean> = input<boolean>(false)
	public isPoolScrollTrigger: InputSignal<boolean> = input<boolean>(false)

	public reqOptionsByNextUrl: OutputEmitterRef<DirectionEnum> = output<DirectionEnum>()

	private _destroy$: Subject<void> = new Subject<void>()

	protected statesColumns: Signal<ColumnInterface<OptionsBoardColumnsEnum>[]> = computed(
		(): ColumnInterface<OptionsBoardColumnsEnum>[] => {
			const widget: WidgetInterface<OptionsBoardSettingsInterface> | null = this.widget()
			return [...(widget?.settings?.columns ?? [])]
		},
	)

	protected dynamicPriceRowSettings: WritableSignal<RowInterface<null> | null> = signal<RowInterface<null> | null>(
		null,
	)
	protected dynamicPriceRowPositionPx: WritableSignal<number> = signal<number>(-1) // Отступ от верха в пикселях

	constructor() {
		effect((): void => {
			this._calculateDynamicRowPricePosition(this.liveTicker(), this.options())
		})
	}

	private _calculateDynamicRowPricePosition(
		liveTicker: LiveTickerInterface | null,
		options: OptionContractInterface[],
	): void {
		if (!liveTicker || !options.length) return

		const rowHeight: number = 40
		const currentPrice: number | undefined = liveTicker.currentPrices.price
		if (!currentPrice) return

		const avgIndex: number = options.findIndex(
			(optionContract: OptionContractInterface, index: number): boolean => {
				const nextOptionContract: OptionContractInterface | undefined = options[index + 1]
				if (!nextOptionContract) return false

				return (
					optionContract.details.strike_price <= currentPrice &&
					nextOptionContract.details.strike_price >= currentPrice
				)
			},
		)

		const latestRowSettings: RowInterface<null> | null = this.dynamicPriceRowSettings()

		// Так как avgIndex равен Среднему элементу с ценой, мы получили его индекс в массиве, а визуально он расположен на + 1,
		// а + 1 даёт его top position Y
		const newPriceRowPositionPx: number = rowHeight * (avgIndex + 1)
		const newIndexRow: number = avgIndex + 1

		if (latestRowSettings?.index === newIndexRow) return

		this.dynamicPriceRowPositionPx.set(newPriceRowPositionPx)
		this.dynamicPriceRowSettings.set(
			avgIndex
				? {
						id: this.dynamicRowIdConstant,
						index: newIndexRow,
						item: null,
					}
				: latestRowSettings,
		)
	}

	private _listenScrollInTable(): void {
		if (!this.optionBoardRef) return
		const tableScrollContainerEl: HTMLDivElement | null =
			this.optionBoardRef?.nativeElement.querySelector('.content-scroll')
		if (!tableScrollContainerEl) return

		fromEvent(tableScrollContainerEl, 'scroll')
			.pipe(takeUntil(this._destroy$), throttleTime(50))
			.subscribe((): void => {
				const dynamicPriceRowEl: HTMLDivElement | undefined = this.dynamicPriceRowRef?.nativeElement
				if (!dynamicPriceRowEl) return

				const dynamicPriceRowHeight: number = dynamicPriceRowEl.clientHeight
				const tableScrollContainerRect: DOMRect = tableScrollContainerEl.getBoundingClientRect()
				const currentDynamicRowPosition: number = this.dynamicPriceRowPositionPx()
				const threshold: number = tableScrollContainerRect.height
				const topScrollbarPosition: number = tableScrollContainerEl.scrollTop
				const bottomScrollbarPosition: number =
					tableScrollContainerEl.scrollTop + tableScrollContainerRect.height
				const isReachedTop: boolean = bottomScrollbarPosition > currentDynamicRowPosition + threshold
				const isReachedBottom: boolean =
					topScrollbarPosition < currentDynamicRowPosition - (threshold - dynamicPriceRowHeight * 2)

				if (!isReachedTop && !isReachedBottom) {
					dynamicPriceRowEl.classList.remove('fixed-top', 'fixed-bottom')
				} else if (isReachedTop) {
					if (!dynamicPriceRowEl.classList.contains('fixed-top')) {
						dynamicPriceRowEl.classList.add('fixed-top')
						dynamicPriceRowEl.classList.remove('fixed-bottom')
					}
				} else if (isReachedBottom) {
					if (!dynamicPriceRowEl.classList.contains('fixed-bottom')) {
						dynamicPriceRowEl.classList.add('fixed-bottom')
						dynamicPriceRowEl.classList.remove('fixed-top')
					}
				}
			})
	}

	protected sendReqOptionsByNextUrl(dir: DirectionEnum): void {
		this.reqOptionsByNextUrl.emit(dir)
	}

	ngAfterViewInit(): void {
		this._listenScrollInTable()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
